import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { WhatsAppContactCard } from "@/components/WhatsAppContactCard";
import { supabase } from "@/lib/supabase";
import {
  batchHasPublicDetailPage,
  batchPublicPrimaryName,
  batchShownOnPublicList,
  newsListedCount,
  projectsListedCount,
} from "@/lib/batchDisplay";
import { maxPublicBatchAlYear } from "@/lib/publicBatchVisibility";

type Batch = {
  id: string;
  slug: string;
  logo_url: string | null;
  official_title: string | null;
  custom_title: string | null;
  description: string | null;
  exco_photo_url: string | null;
  al_year: number | null;
  projects?: { count: number }[];
  news?: { count: number }[];
};

function batchMatchesSearch(b: Batch, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const primary = batchPublicPrimaryName(b).toLowerCase();
  const custom = (b.custom_title ?? "").toLowerCase();
  const official = (b.official_title ?? "").toLowerCase();
  const slug = (b.slug ?? "").toLowerCase();
  const desc = (b.description ?? "").toLowerCase();
  const al = b.al_year != null ? String(b.al_year) : "";
  return (
    primary.includes(q) ||
    custom.includes(q) ||
    official.includes(q) ||
    slug.includes(q) ||
    desc.includes(q) ||
    al.includes(q)
  );
}

function batchProjectsNewsLabels(b: Batch): string[] {
  const labels: string[] = [];
  const p = projectsListedCount(b);
  if (p > 0) labels.push(p === 1 ? "1 project" : `${p} projects`);
  const n = newsListedCount(b);
  if (n > 0) labels.push(n === 1 ? "1 news item" : `${n} news items`);
  return labels;
}

export function BatchesListPage() {
  const [rows, setRows] = useState<Batch[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredRows = useMemo(
    () => rows.filter((b) => batchMatchesSearch(b, searchQuery)),
    [rows, searchQuery]
  );

  useEffect(() => {
    const cutoff = maxPublicBatchAlYear();
    void supabase
      .from("batches")
      .select(
        "id,slug,logo_url,official_title,custom_title,description,exco_photo_url,al_year,projects(count),news(count)"
      )
      .or(`al_year.is.null,al_year.lte.${cutoff}`)
      .order("al_year", { ascending: false, nullsFirst: false })
      .then(({ data }) => {
        const all = (data as Batch[]) ?? [];
        setRows(all.filter(batchShownOnPublicList));
      });
  }, []);

  const cardClass =
    "flex h-full flex-col rounded-2xl border border-brand-gray bg-white p-6 shadow-sm transition hover:border-brand-maroon/40";

  return (
    <div className="mx-auto max-w-6xl px-4 py-14">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-maroon">
        Batches
      </p>
      <h1 className="mt-2 font-display text-4xl font-bold text-brand-blue">
        Batches
      </h1>
      <p className="mt-4 max-w-2xl text-brand-blue/85">
        Year groups listed here have shared at least one of: a project, description,
        ExCo photo, news, logo, or custom name. A dedicated batch page opens when
        they add a project, news, or ExCo photo.
      </p>
      {rows.length > 0 ? (
        <div className="mt-8 max-w-md">
          <label htmlFor="batches-list-search" className="sr-only">
            Search batches
          </label>
          <input
            id="batches-list-search"
            type="search"
            autoComplete="off"
            placeholder="Search by batch name, year, or description…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-brand-gray bg-white px-4 py-2.5 text-sm text-brand-blue shadow-sm placeholder:text-brand-blue/45 focus:border-brand-maroon/50 focus:outline-none focus:ring-2 focus:ring-brand-maroon/20"
          />
        </div>
      ) : null}

      <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.length === 0 && (
          <li className="text-sm text-brand-blue/60 col-span-full">
            No batches are listed yet. When a batch adds any of the content above in
            the admin portal, it can appear here.
          </li>
        )}
        {rows.length > 0 && filteredRows.length === 0 && (
          <li className="text-sm text-brand-blue/60 col-span-full">
            No batches match your search.
          </li>
        )}
        {filteredRows.map((b) => {
          const hasPage = batchHasPublicDetailPage(b);
          const labels = batchProjectsNewsLabels(b);
          const inner = (
            <>
              <div className="flex gap-4">
                {b.logo_url ? (
                  <img
                    src={b.logo_url}
                    alt=""
                    className="h-16 w-16 shrink-0 rounded-xl border border-brand-gray bg-white object-contain p-1.5"
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <span className="font-display text-lg font-bold text-brand-blue block">
                    {batchPublicPrimaryName(b)}
                  </span>
                  {b.custom_title?.trim() ? (
                    <span className="mt-1 block text-sm text-brand-blue/70">
                      {b.custom_title.trim()}
                    </span>
                  ) : null}
                </div>
              </div>
              {labels.length > 0 ? (
                <p className="mt-3 text-xs leading-relaxed text-brand-blue/70">
                  {labels.join(" · ")}
                </p>
              ) : null}
              {hasPage ? (
                <span className="mt-4 text-xs font-bold uppercase tracking-wide text-brand-maroon">
                  View batch →
                </span>
              ) : (
                <span className="mt-4 text-xs font-semibold text-brand-blue/55">
                  Public batch page opens after a project, news post, or ExCo photo
                  is added.
                </span>
              )}
            </>
          );

          return (
            <li key={b.id}>
              {hasPage ? (
                <Link
                  to={`/alumni/batches/${b.slug}`}
                  className={cardClass}
                >
                  {inner}
                </Link>
              ) : (
                <div className={`${cardClass} cursor-default hover:border-brand-gray`}>
                  {inner}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <WhatsAppContactCard
        className="mt-14 max-w-2xl"
        title="Register your batch"
        description="Use this if your year group needs a batch page (name, logo, projects, or listing on this page). Please contact us via WhatsApp for batch registration only."
        prefilledMessage="Hello, I would like to register or set up our batch page with the OBA Zahira College Mawanella (please include A/L year or batch name if known)."
        buttonLabel="Message us on WhatsApp"
      />
    </div>
  );
}
